/**
 * ConfirmDetails - Component for confirming pre-filled patient information.
 * 
 * For follow-up questionnaires, this shows the patient's existing information
 * and allows them to confirm or edit before proceeding.
 * 
 * IMPORTANT: This is NOT a skip screen - patients must explicitly confirm.
 */
import React, { useState } from 'react';

/**
 * ConfirmDetails component.
 * 
 * @param {Object} props
 * @param {Object} props.prefillData - Pre-filled data from backend
 * @param {Function} props.onConfirm - Callback when details are confirmed
 * @param {Function} props.onEdit - Callback when user wants to edit (redirects to full form)
 * @param {Object} props.clientConfig - Client configuration for branding
 */
function ConfirmDetails({ prefillData, onConfirm, onEdit, clientConfig }) {
    const [isConfirming, setIsConfirming] = useState(false);

    const personalDetails = prefillData?.personal_details || {};
    const shippingAddress = prefillData?.shipping_address || {};

    const handleConfirm = () => {
        setIsConfirming(true);
        // Small delay for UX
        setTimeout(() => {
            onConfirm(prefillData);
        }, 300);
    };

    const formatPhone = (phone) => {
        if (!phone) return 'Not provided';
        // Basic US phone formatting
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    const formatAddress = () => {
        const parts = [
            shippingAddress.address,
            shippingAddress.city,
            shippingAddress.state,
            shippingAddress.zip_code
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'Not provided';
    };

    return (
        <div className="confirm-details" style={styles.container}>
            <div style={styles.card}>
                <div style={styles.header}>
                    <svg style={styles.headerIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <polyline points="17 11 19 13 23 9" />
                    </svg>
                    <h2 style={styles.title}>Confirm Your Details</h2>
                </div>

                <p style={styles.subtitle}>
                    Please verify your information is still correct before continuing.
                </p>

                {/* Personal Details Section */}
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Personal Information</h3>
                    <div style={styles.detailsGrid}>
                        <DetailRow label="Name" value={`${personalDetails.first_name || ''} ${personalDetails.last_name || ''}`} />
                        <DetailRow label="Email" value={personalDetails.email || 'Not provided'} />
                        <DetailRow label="Phone" value={formatPhone(personalDetails.phone)} />
                        <DetailRow label="Date of Birth" value={personalDetails.date_of_birth || 'Not provided'} />
                    </div>
                </div>

                {/* Shipping Address Section */}
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Shipping Address</h3>
                    <div style={styles.detailsGrid}>
                        <DetailRow label="Address" value={formatAddress()} fullWidth />
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={styles.actions}>
                    <button
                        onClick={handleConfirm}
                        disabled={isConfirming}
                        style={{
                            ...styles.primaryButton,
                            opacity: isConfirming ? 0.7 : 1,
                            cursor: isConfirming ? 'wait' : 'pointer'
                        }}
                    >
                        {isConfirming ? 'Confirming...' : 'Confirm & Continue'}
                    </button>

                    <button
                        onClick={onEdit}
                        disabled={isConfirming}
                        style={styles.secondaryButton}
                    >
                        Edit Information
                    </button>
                </div>

                <p style={styles.note}>
                    If any information has changed, click "Edit Information" to update it.
                </p>
            </div>
        </div>
    );
}

/**
 * DetailRow - Single row in the details display.
 */
function DetailRow({ label, value, fullWidth }) {
    return (
        <div style={{
            ...styles.detailRow,
            gridColumn: fullWidth ? '1 / -1' : undefined
        }}>
            <span style={styles.detailLabel}>{label}</span>
            <span style={styles.detailValue}>{value}</span>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--zyvgo-background, #FAFAFA)',
        padding: '20px',
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '560px',
        width: '100%',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '12px',
    },
    headerIcon: {
        width: '32px',
        height: '32px',
        color: 'var(--zyvgo-primary, #222222)',
    },
    title: {
        fontSize: '24px',
        fontWeight: '600',
        color: 'var(--zyvgo-text, #1a1a1a)',
        margin: 0,
        fontFamily: 'var(--zyvgo-font-family, inherit)',
    },
    subtitle: {
        fontSize: '16px',
        color: 'var(--zyvgo-text-secondary, #666666)',
        marginBottom: '32px',
        lineHeight: '1.5',
    },
    section: {
        marginBottom: '24px',
        padding: '20px',
        background: 'var(--zyvgo-section-bg, #f8f9fa)',
        borderRadius: '12px',
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--zyvgo-text-secondary, #888888)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '16px',
        marginTop: 0,
    },
    detailsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '16px',
    },
    detailRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    detailLabel: {
        fontSize: '12px',
        color: 'var(--zyvgo-text-secondary, #888888)',
        fontWeight: '500',
    },
    detailValue: {
        fontSize: '15px',
        color: 'var(--zyvgo-text, #1a1a1a)',
        fontWeight: '500',
    },
    actions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '32px',
    },
    primaryButton: {
        width: '100%',
        padding: '16px 24px',
        backgroundColor: 'var(--zyvgo-primary, #222222)',
        color: 'white',
        border: 'none',
        borderRadius: '28px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    secondaryButton: {
        width: '100%',
        padding: '14px 24px',
        backgroundColor: 'transparent',
        color: 'var(--zyvgo-primary, #222222)',
        border: '2px solid var(--zyvgo-border, #e1e5e9)',
        borderRadius: '28px',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    note: {
        fontSize: '13px',
        color: 'var(--zyvgo-text-secondary, #999999)',
        textAlign: 'center',
        marginTop: '16px',
        lineHeight: '1.4',
    },
};

export default ConfirmDetails;
